import styled from '@emotion/styled'
import {
  ExtranetDownloadTicketAttachmentDto,
  ExtranetTicketAttachmentDto,
} from '@pharmac-extranet/extranet-api'
import { ExtranetContainer } from '@pharmac-extranet/shared'
import { PharmacH6, PharmacIcon, PharmacText } from '@pharmac/ui'
import { createElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { createTicketAttachmentFilename } from './util'

type TicketAttachmentsProps = {
  ticketId: string
  items: ExtranetTicketAttachmentDto[]
}

type TicketAttachmentProps = {
  item: ExtranetDownloadTicketAttachmentDto & ExtranetTicketAttachmentDto
}

type NewTicketAttachmentProps = {
  attachment: File
  removeAttachment: () => void
}

const AttachmentContainer = styled.div`
  div.inner {
    width: 93px;
    height: 54px;

    border-radius: 5px;
    border: 1px solid var(--color-accent-4);

    transition: var(--transition-cubic-bezier);
    &:hover {
      transform: var(--button-primary-transform-hover);
      box-shadow: var(--shadow-tiny);
    }

    &:not(last-of-type) {
      margin-right: var(--spacing-small);
    }

    a {
      display: block;
      width: 100%;
      height: 100%;

      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;

      img {
        height: 100%;
        width: 100%;
        object-fit: contain;
      }

      svg {
        color: var(--color-primary);
        height: 34px;
        width: 34px;
      }
    }
  }
`

const TicketAttachment = ({ item }: TicketAttachmentProps) => {
  const img =
    item.mime.includes('image') &&
    createElement('img', {
      alt: item.originalFilename,
      src: `data:${item.mime};base64,${item.base64}`,
    })

  const filename = createTicketAttachmentFilename(item.originalFilename)

  return (
    <AttachmentContainer>
      <div className="inner">
        <a
          title={item.originalFilename}
          target="_blank"
          download={item.originalFilename}
          href={`data:${item.mime};base64,${item.base64}`}
        >
          {item.mime.includes('pdf') ? <PharmacIcon icon="fileText" /> : img}
        </a>
      </div>
      <div
        className="filename"
        title={item.originalFilename}
      >
        {filename}
      </div>
    </AttachmentContainer>
  )
}

export const TicketAttachments = ({ ticketId, items }: TicketAttachmentsProps) => {
  const [attachments, setAttachments] = useState<JSX.Element[]>([])
  const { customer } = useCustomerContext()
  const { t } = useTranslation()
  const downloadImages = useCallback(
    async (items: ExtranetTicketAttachmentDto[]) => {
      return await Promise.all(
        items.map(async (item) => {
          const download = await api.tickets.downloadAttachmentFile(
            customer!.id,
            ticketId,
            item.filename
          )

          return {
            ...item,
            ...download,
          }
        })
      )
    },
    [customer?.id]
  )

  useEffect(() => {
    if (items.length > 0) {
      downloadImages(items).then((itemsWithImages) => {
        const mappedAttachments = itemsWithImages.map((i) => {
          return (
            <TicketAttachment
              item={i}
              key={i.filename}
            />
          )
        })

        setAttachments(mappedAttachments)
      })
    }
  }, [items, customer?.id])

  return (
    <ExtranetContainer flexColumn>
      <ExtranetContainer full>
        <PharmacH6 paddingMediumBottom>{t('contact.attachments')}</PharmacH6>
      </ExtranetContainer>
      <ExtranetContainer
        full
        flexRow
        flexWrap
      >
        {attachments}
      </ExtranetContainer>
    </ExtranetContainer>
  )
}

export const NewTicketAttachment = ({ attachment, removeAttachment }: NewTicketAttachmentProps) => {
  return (
    <ExtranetContainer
      marginMediumRight
      marginLargeTop
      flexColumn
    >
      <ExtranetContainer>
        <ExtranetContainer
          style={{
            border: '2px solid var(--color-accent-4)',
            borderRadius: '10px',
          }}
        >
          {attachment?.type.includes('pdf') ? (
            <PharmacIcon
              icon="fileText"
              size="60px"
            />
          ) : (
            <img
              style={{ borderRadius: '10px' }}
              height="60px"
              src={URL.createObjectURL(attachment)}
              alt=""
            />
          )}
        </ExtranetContainer>
        <ExtranetContainer
          onClick={removeAttachment}
          style={{
            cursor: 'pointer',
            backgroundColor: 'var(--color-primary)',
            padding: '6px',
            borderRadius: '20px',
            position: 'relative',
            right: 'var(--spacing-medium)',
            bottom: 'var(--spacing-small)',
          }}
        >
          <PharmacIcon
            stroke="var(--color-accent-1)"
            icon="trash2"
            size="18px"
          />
        </ExtranetContainer>
      </ExtranetContainer>
      <ExtranetContainer paddingSmallVertical>
        <PharmacText>{attachment && createTicketAttachmentFilename(attachment.name)}</PharmacText>
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
